import { type ComponentType, Fragment, useMemo } from "react";
import { intersperse, uniqWith, init } from "ramda";
import { t } from "@lingui/core/macro";

import { DocsIndexDoc, MachinesIndexDoc, ProjectsIndexDoc } from "./indices";
import { Highlight } from "./highlight";
import * as S from "./styled";

/**
 * Search result type for use within this codebase.
 *
 * There seems to be a bug in the flexsearch types which does not allow for
 * correctly typing "enriched" search results, so we have to define our own
 * type here to work around dealing with an `unknown` (or incorrect) type.
 */
export type SearchResult<T, R> = Array<{
  /**
   * Canonical result ID.
   *
   * This is typically a UUID, but can be any type.
   */
  id: T;

  /**
   * Search result document content which may be an entirely different type
   * than the target entity type.
   */
  doc: R;
}>;

function highlighter(query: string) {
  const Hl = ({ text }: { text: string }) => (
    <Highlight text={text} query={query} />
  );

  return Hl;
}

export type SearchResultItemProps<T> = {
  resultDoc: T;
  allResultDocs: T[];
  Highlighter: ComponentType<{ text: string }>;
};

type SearchResultListProps<T> = {
  /** User-entered search query string */
  query: string;

  /** List of search results to render. */
  results: SearchResult<string, T>;

  /** Function to generate the URL for each result */
  getResultUrl: (doc: T) => string;

  /** Component to render each result in the list */
  resultItemComponent: ComponentType<SearchResultItemProps<T>>;

  /** Called when a result is clicked, e.g. to reset the search box */
  onClick: () => void;
};

export const SearchResultList = <T,>({
  results,
  query,
  getResultUrl,
  resultItemComponent: ResultItemComponent,
  onClick,
}: SearchResultListProps<T>) => {
  const Hl = highlighter(query);
  const allResultDocs = results.map((r) => r.doc);
  return (
    <>
      {results.map((result, i) => (
        <S.ResultItem key={i} to={getResultUrl(result.doc)} onClick={onClick}>
          <ResultItemComponent
            resultDoc={result.doc}
            allResultDocs={allResultDocs}
            Highlighter={Hl}
          />
        </S.ResultItem>
      ))}
    </>
  );
};

export const MachineSearchResultItem = ({
  resultDoc,
  allResultDocs,
  Highlighter,
}: SearchResultItemProps<MachinesIndexDoc>) => {
  const anyResultsFromAnotherOrg = useMemo(
    () => allResultDocs.some((doc) => !doc.inCurrentOrg),
    [allResultDocs]
  );

  const serialInMachineName = resultDoc.name
    .toLowerCase()
    .includes(resultDoc.hardware.serial.toLowerCase());

  return (
    <>
      <S.ResultTitle>
        <Highlighter text={resultDoc.name} />
        {!serialInMachineName && (
          <S.Muted>
            &nbsp;&nbsp;
            <Highlighter text={resultDoc.hardware.serial} />
          </S.Muted>
        )}
      </S.ResultTitle>
      {anyResultsFromAnotherOrg && (
        <S.Muted>
          <Highlighter text={resultDoc.orgName} />
        </S.Muted>
      )}
    </>
  );
};

export const DocSearchResultItem = ({
  resultDoc,
  Highlighter,
}: SearchResultItemProps<DocsIndexDoc>) => {
  // Display only the breadcrumb trail, not the final segment (the page title).
  const breadcrumbs =
    resultDoc.breadcrumbs.length === 1
      ? resultDoc.breadcrumbs
      : init(resultDoc.breadcrumbs);

  return (
    <>
      <S.ResultTitle>
        <Highlighter text={resultDoc.title} />
      </S.ResultTitle>
      <S.Muted>{breadcrumbs.join(" / ")}</S.Muted>
    </>
  );
};

export const ProjectSearchResultItem = ({
  resultDoc,
  Highlighter,
}: SearchResultItemProps<ProjectsIndexDoc>) => {
  // Materials deduplicated for cases where both tools use the same material.
  const materials = uniqWith((a, b) => a.id === b.id, resultDoc.materials).map(
    (m, j) => <Highlighter key={j} text={m.name} />
  );

  const revisionCount = t`search.revision-count ${resultDoc.revisionCount}`;

  return (
    <>
      <S.ResultTitle>
        <Highlighter text={resultDoc.name} />
      </S.ResultTitle>
      <S.Muted>
        {intersperse(<S.Slash />, materials).map((m, j) => (
          <Fragment key={j}>{m}</Fragment>
        ))}
        <S.Bar />
        <Highlighter text={resultDoc.owner.name} />
        <S.Bar />
        {revisionCount}
      </S.Muted>
    </>
  );
};
